import React, { useId, useMemo, useState } from "react";
import { Button } from "@heroui/react";

const ASSETS_VERSION = "2026-04-01";
const YOUR_EUROPE =
  "https://europa.eu/youreurope/business/dealing-with-customers/consumer-contracts-guarantees/consumer-guarantees/index_en.htm";

const LOCALES = [
  { value: "de", label: "Deutsch" },
  { value: "en", label: "English" },
  { value: "fr", label: "Français" },
  { value: "es", label: "Español" },
  { value: "it", label: "Italiano" },
  { value: "nl", label: "Nederlands" },
  { value: "pl", label: "Polski" }
];

const COPY = {
  de: {
    vendor: "Beispiel Marke",
    title: "Premium Laufschuh Pro",
    price: "129,00 €",
    atc: "In den Warenkorb",
    desc: "Leichtes Modell für Training und Alltag. Lieferzeit 2–4 Werktage.",
    expand: "Gesetzliche Gewährleistung anzeigen",
    popupOpen: "Gewährleistungsinformationen öffnen",
    moreInfo: "Weitere Informationen",
    downloadPdf: "PDF herunterladen",
    garanTitle: "Herstellergarantie (GARAN)",
    noticeAlt: "EU-Hinweis zur gesetzlichen Gewährleistung",
    aiAlt: "KI-Kennzeichnung"
  },
  en: {
    vendor: "Sample Brand",
    title: "Premium Running Shoe Pro",
    price: "€129.00",
    atc: "Add to cart",
    desc: "Lightweight model for training and everyday use. Ships in 2–4 business days.",
    expand: "Show legal guarantee notice",
    popupOpen: "Open guarantee information",
    moreInfo: "More information",
    downloadPdf: "Download PDF",
    garanTitle: "Commercial guarantee (GARAN)",
    noticeAlt: "EU legal guarantee notice",
    aiAlt: "AI transparency label"
  }
};

function noticeUrl(lang) {
  return `/eu-guarantees/${ASSETS_VERSION}/notices/${lang}.svg`;
}

function noticePdfUrl(lang) {
  return `/eu-guarantees/${ASSETS_VERSION}/pdfs/${lang}.pdf`;
}

function garanUrl({ years, brand, model, variant }) {
  const q = new URLSearchParams({
    years: String(years),
    brand: brand || "",
    model: model || ""
  });
  return `/eu-guarantees/${ASSETS_VERSION}/garan/${variant}.svg?${q}`;
}

function aiLabelUrl(kind = "generated", variant = "black") {
  return `/eu-ai-labels/${kind}/${variant}.svg`;
}

export function LandingGuaranteePreview() {
  const uid = useId().replace(/:/g, "");
  const [noticeMode, setNoticeMode] = useState("expandable");
  const [lang, setLang] = useState("de");
  const [theme, setTheme] = useState("light");
  const [years, setYears] = useState("5");
  const [brand, setBrand] = useState("DXL Demo");
  const [model, setModel] = useState("Pro X");
  const [showAiLabel, setShowAiLabel] = useState(true);
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [garanExpanded, setGaranExpanded] = useState(false);

  const copy = COPY[lang] || COPY.en;
  const showGaran = Number(years) > 2;
  const noticeSrc = useMemo(() => noticeUrl(lang), [lang]);
  const pdfSrc = useMemo(() => noticePdfUrl(lang), [lang]);
  const garanNestedSrc = useMemo(
    () => garanUrl({ years, brand, model, variant: "nested" }),
    [years, brand, model]
  );
  const garanFullSrc = useMemo(
    () => garanUrl({ years, brand, model, variant: "full" }),
    [years, brand, model]
  );
  const aiSrc = useMemo(() => aiLabelUrl("generated", theme === "dark" ? "white" : "black"), [theme]);

  const noticeLinks = (
    <p className="landing-eug__links">
      <a href={YOUR_EUROPE} target="_blank" rel="noopener noreferrer">
        {copy.moreInfo}
      </a>
      <a href={pdfSrc} target="_blank" rel="noopener noreferrer">
        {copy.downloadPdf}
      </a>
    </p>
  );

  const noticeImg = (
    <img
      className="landing-eug__notice-img"
      src={noticeSrc}
      alt={copy.noticeAlt}
      width={595}
      height={842}
      loading="lazy"
    />
  );

  let noticeBlock;
  if (noticeMode === "full") {
    noticeBlock = (
      <div className="landing-eug__notice">
        {noticeImg}
        {noticeLinks}
      </div>
    );
  } else if (noticeMode === "popup") {
    noticeBlock = (
      <div className="landing-eug__notice">
        <button
          type="button"
          className="landing-eug__trigger"
          onClick={() => setNoticeOpen(true)}
          aria-haspopup="dialog"
        >
          {copy.popupOpen}
        </button>
        {noticeLinks}
      </div>
    );
  } else {
    noticeBlock = (
      <div className="landing-eug__notice">
        <details className="landing-eug__details">
          <summary className="landing-eug__expand-summary">{copy.expand}</summary>
          {noticeImg}
        </details>
        {noticeLinks}
      </div>
    );
  }

  return (
    <div className="landing-preview">
      <header className="landing-preview__intro">
        <p className="landing-services__eyebrow">Live storefront preview</p>
        <h2 className="landing-section-title">How it looks in the shop</h2>
        <p className="landing-preview__lead">
          Real EU notice SVGs, GARAN labels, and AI transparency marks — the same assets served to
          storefronts.
        </p>
      </header>

      <div className="landing-preview__grid">
        <form className="landing-preview__panel" onSubmit={(e) => e.preventDefault()}>
          <label className="landing-preview__field">
            <span>Notice layout</span>
            <select value={noticeMode} onChange={(e) => setNoticeMode(e.target.value)}>
              <option value="full">Full notice</option>
              <option value="expandable">Expandable</option>
              <option value="popup">Popup</option>
            </select>
          </label>

          <label className="landing-preview__field">
            <span>Language</span>
            <select value={lang} onChange={(e) => setLang(e.target.value)}>
              {LOCALES.map((locale) => (
                <option key={locale.value} value={locale.value}>
                  {locale.label}
                </option>
              ))}
            </select>
          </label>

          <label className="landing-preview__field">
            <span>Shop theme</span>
            <select value={theme} onChange={(e) => setTheme(e.target.value)}>
              <option value="light">Light</option>
              <option value="dark">Dark</option>
            </select>
          </label>

          <label className="landing-preview__field">
            <span>Manufacturer guarantee</span>
            <select
              value={years}
              onChange={(e) => {
                setYears(e.target.value);
                setGaranExpanded(false);
              }}
            >
              <option value="0">None</option>
              <option value="2">2 years (legal only)</option>
              <option value="2.5">2.5 years</option>
              <option value="3">3 years</option>
              <option value="5">5 years</option>
              <option value="10">10 years</option>
              <option value="25">25 years</option>
            </select>
          </label>

          <label className="landing-preview__field">
            <span>Brand</span>
            <input
              type="text"
              value={brand}
              onChange={(e) => {
                setBrand(e.target.value);
                setGaranExpanded(false);
              }}
              disabled={!showGaran}
            />
          </label>

          <label className="landing-preview__field">
            <span>Model</span>
            <input
              type="text"
              value={model}
              onChange={(e) => {
                setModel(e.target.value);
                setGaranExpanded(false);
              }}
              disabled={!showGaran}
            />
          </label>

          <label className="landing-preview__field landing-preview__field--check">
            <input
              type="checkbox"
              checked={showAiLabel}
              onChange={(e) => setShowAiLabel(e.target.checked)}
            />
            <span>Show AI label</span>
          </label>
        </form>

        <div className={`landing-preview__shop ${theme === "dark" ? "is-dark" : ""}`}>
          <div className="landing-preview__pdp">
            <div className="landing-preview__media" aria-hidden="true">
              {showAiLabel ? (
                <img className="landing-preview__ai-label" src={aiSrc} alt={copy.aiAlt} />
              ) : null}
            </div>
            <div className="landing-preview__info">
              <p className="landing-preview__vendor">{brand || copy.vendor}</p>
              <h3 className="landing-preview__product-title">{copy.title}</h3>
              {model ? <p className="landing-preview__model">{model}</p> : null}
              <p className="landing-preview__price">{copy.price}</p>
              <button type="button" className="landing-preview__atc">
                {copy.atc}
              </button>
              <p className="landing-preview__desc">{copy.desc}</p>

              <div className="landing-eug">
                {noticeBlock}

                {showGaran ? (
                  <div className="landing-eug__garan-block">
                    <p className="landing-eug__garan-title">{copy.garanTitle}</p>
                    <button
                      type="button"
                      className="landing-eug__garan"
                      onClick={() => setGaranExpanded((open) => !open)}
                      aria-expanded={garanExpanded}
                      aria-label={copy.garanTitle}
                    >
                      <img
                        className="landing-eug__garan-img"
                        src={garanExpanded ? garanFullSrc : garanNestedSrc}
                        alt={copy.garanTitle}
                        width={garanExpanded ? 595 : 369}
                        height={garanExpanded ? 842 : 57}
                        loading="lazy"
                      />
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      {noticeOpen && noticeMode === "popup" ? (
        <div className="landing-eug__dialog" role="dialog" aria-modal="true" aria-labelledby={`notice-title-${uid}`}>
          <div className="landing-eug__dialog-panel">
            <div className="landing-eug__dialog-head">
              <h3 id={`notice-title-${uid}`}>{copy.noticeAlt}</h3>
              <Button variant="ghost" size="sm" onPress={() => setNoticeOpen(false)}>
                Close
              </Button>
            </div>
            {noticeImg}
            {noticeLinks}
          </div>
        </div>
      ) : null}
    </div>
  );
}
